import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Link,
    Paper,
    Grid,
    IconButton,
    InputAdornment,
    useTheme,
    useMediaQuery,
    MobileStepper,
    Fade,
    Zoom,
    Grow,
    Divider
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    ArrowForward,
    Lock,
    Fingerprint
} from '@mui/icons-material';
import { keyframes, styled } from '@mui/material/styles';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

// Styled components
const GradientButton = styled(Button)(({ theme }) => ({
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    color: 'white',
    padding: '10px 24px',
    borderRadius: '50px',
    fontWeight: 'bold',
    textTransform: 'none',
    fontSize: '1rem',
    boxShadow: '0 4px 15px rgba(33, 150, 243, 0.3)',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(33, 150, 243, 0.4)',
        '&::after': {
            opacity: 1
        }
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        opacity: 0,
        transition: 'opacity 0.3s ease'
    },
    '& span': {
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    }
}));

const AnimatedTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        '& fieldset': {
            borderColor: theme.palette.grey[300],
        },
        '&:hover fieldset': {
            borderColor: theme.palette.primary.light,
            boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.1)'
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.1)'
        }
    },
    '& .MuiInputLabel-root': {
        color: theme.palette.text.secondary,
        '&.Mui-focused': {
            color: theme.palette.primary.main
        }
    }
}));

const waveGradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const carouselItems = [
    {
        label: 'NYGGS ERP',
        description: 'Save time and boost productivity with NYGGS. A cloud-based SaaS ERP solution to drive your business goals.',
        buttonText: 'Check Us',
        icon: <Lock fontSize="large" />
    },
    {
        label: 'Project & Planing',
        description: 'Streamline project budgeting, planning, and execution with ease.',
        buttonText: 'Explore',
        icon: <Lock fontSize="large" />
    },
    {
        label: 'Inventory Management Software',
        description: "Optimize inventory with smart material tracking, seamless reconciliation, and AI-driven demand forecasting.",
        buttonText: 'Explore',
        icon: <Lock fontSize="large" />
    },

];

const LoginScreen = ({ companyLogo, setPassword, setUser, password, user, navigateToModules }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [activeStep, setActiveStep] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [focusedField, setFocusedField] = useState(null);

    const handleStepChange = (step) => setActiveStep(step);
    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = (e) => e.preventDefault();
    const handleFocus = (fieldName) => setFocusedField(fieldName);
    const handleBlur = () => setFocusedField(null);

    // Show adornment only when not mobile and field is focused
    const showAdornment = (fieldName) => !isMobile && focusedField === fieldName;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: isMobile ? 2 : 0,
                overflow: 'hidden',
                '& *::-webkit-scrollbar': {
                    display: 'none'
                },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none'
            }}
        >
            <Grow in={true} timeout={500}>
                <Paper
                    elevation={isMobile ? 2 : 4}
                    sx={{
                        width: '100%',
                        maxWidth: isMobile ? '450px' : '1000px',
                        overflow: 'hidden',
                        borderRadius: '16px',
                        transition: 'all 0.4s ease',
                        '&:hover': {
                            boxShadow: theme.shadows[10]
                        }
                    }}
                >
                    <Grid container sx={{ height: isMobile ? 'auto' : '530px' }}>
                        {/* Left Section - Login Form */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                p: isMobile ? 3 : 4,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                background: 'white'
                            }}>
                                <Box sx={{
                                    // border:"1px solid red",
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    mb: 2,
                                    mt: -4
                                }}>
                                    <Zoom in={true} timeout={800}>
                                        <Box sx={{ mb: 1 }}>
                                            <img
                                                src={companyLogo}
                                                alt="Company Logo"
                                                style={{
                                                    height: isMobile ? '50px' : '60px',
                                                    maxWidth: isMobile ? '220px' : '260px',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </Box>
                                    </Zoom>

                                    <Fade in={true} timeout={1000}>
                                        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{
                                            fontWeight: 'bold',
                                            color: theme.palette.text.primary,
                                            mb: 1,
                                            textAlign: 'center'
                                        }}>
                                            Sign In
                                        </Typography>
                                    </Fade>

                                    <Fade in={true} timeout={1200}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0, textAlign: 'center' }}>
                                            Sign in to your enterprise account
                                        </Typography>
                                    </Fade>
                                </Box>

                                <Box component="form" noValidate sx={{ mt: -2 }}>
                                    <Fade in={true} timeout={1400}>
                                        <AnimatedTextField
                                            margin="normal"
                                            required
                                            fullWidth
                                            id="employeeCode"
                                            label="Employee ID"
                                            name="employeeCode"
                                            autoComplete="username"
                                            autoFocus
                                            value={user}
                                            onChange={(e) => setUser(e.target.value)}
                                            onFocus={() => handleFocus('employeeCode')}
                                            onBlur={handleBlur}
                                            InputProps={{
                                                startAdornment: showAdornment('employeeCode') ? (
                                                    <InputAdornment position="start">
                                                        <Fingerprint color="primary" />
                                                    </InputAdornment>
                                                ) : null
                                            }}
                                            sx={{ mb: 2 }}
                                            size={isMobile ? 'small' : 'medium'}
                                        />
                                    </Fade>

                                    <Fade in={true} timeout={1600}>
                                        <AnimatedTextField
                                            margin="normal"
                                            required
                                            fullWidth
                                            name="password"
                                            label="Password"
                                            type={showPassword ? 'text' : 'password'}
                                            id="password"
                                            autoComplete="current-password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            onFocus={() => handleFocus('password')}
                                            onBlur={handleBlur}
                                            InputProps={{
                                                startAdornment: showAdornment('password') ? (
                                                    <InputAdornment position="start">
                                                        <Lock color="primary" />
                                                    </InputAdornment>
                                                ) : null,
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                            size={isMobile ? 'small' : 'medium'}
                                                            sx={{
                                                                color: theme.palette.grey[600],
                                                                '&:hover': {
                                                                    color: theme.palette.primary.main
                                                                }
                                                            }}
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                            sx={{ mb: 1 }}
                                            size={isMobile ? 'small' : 'medium'}
                                        />
                                    </Fade>

                                    <Fade in={true} timeout={1800}>
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            mb: 2
                                        }}>
                                            <Link
                                                href="#"
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.secondary,
                                                    textDecoration: 'none',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        color: theme.palette.primary.main,
                                                        fontWeight: '500'
                                                    }
                                                }}
                                            >
                                                Forgot password?
                                            </Link>
                                        </Box>
                                    </Fade>

                                    <Fade in={true} timeout={2000}>
                                        <GradientButton
                                            fullWidth
                                            onClick={navigateToModules}
                                            onMouseEnter={() => setIsHovered(true)}
                                            onMouseLeave={() => setIsHovered(false)}
                                            sx={{ mt: 1, mb: 2.5 }}
                                            disableRipple
                                        >
                                            <span>
                                                {isHovered ? 'Access Modules' : 'Sign In'}
                                                <ArrowForward fontSize="small" />
                                            </span>
                                        </GradientButton>
                                    </Fade>

                                    {!isMobile && (
                                        <Fade in={true} timeout={2200} >
                                            <Divider sx={{ my: 1, color: theme.palette.text.secondary }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Powered By <span style={{ color: "green", fontWeight: 900, }}>NYGGS</span>
                                                </Typography>
                                            </Divider>
                                        </Fade>
                                    )}

                                </Box>


                            </Box>
                        </Grid>

                        {/* Right Section - Carousel */}
                        {!isMobile && (
                            <Grid
                                item
                                md={6}
                                sx={{
                                    background: `linear-gradient(135deg, #1976D2,80%, #64B5F6 10%)`,
                                    // backgroundImage: 'linear-gradient(to bottom right, #1976D2, #64B5F6)',
                                    backgroundSize: '400% 400%',
                                    animation: `${waveGradient} 15s ease infinite`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 6,
                                    position: 'relative',
                                    overflow: 'hidden', // ✅ already here
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        background: 'radial-gradient(circle at 75% 30%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                                    }
                                }}
                            >
                                <AutoPlaySwipeableViews
                                    axis="x"
                                    index={activeStep}
                                    onChangeIndex={handleStepChange}
                                    enableMouseEvents
                                    interval={5000}
                                >
                                    {carouselItems.map((step, index) => (
                                       <Box
                                       key={index}
                                       sx={{
                                           height: '100%',
                                           display: 'flex',
                                           flexDirection: 'column',
                                           alignItems: 'center',
                                           justifyContent: 'center',
                                           p: 4,
                                           textAlign: 'center',
                                           color: 'white',
                                           px: 4, // Adjust horizontal padding
                                           py: 2  // Adjust vertical padding
                                       }}
                                   >
                                            <Fade in={activeStep === index} timeout={500}>
                                                <Box
                                                    sx={{
                                                        width: 120,
                                                        height: 120,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: 'rgba(255,255,255,0.15)',
                                                        borderRadius: '50%',
                                                        mb: 4,
                                                        overflow: 'hidden' // ✅ added here
                                                    }}
                                                >
                                                    {step.icon}
                                                </Box>
                                            </Fade>

                                            <Zoom in={activeStep === index} timeout={800}>
                                                <Typography
                                                    variant="h4"
                                                    gutterBottom
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        mb: 1,
                                                        textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                        whiteSpace: 'normal', // Allow text to wrap
                                                        wordBreak: 'break-word', // Ensure long words break
                                                        textAlign: 'center', // Center the text
                                                        px: 2, // Add some horizontal padding
                                                        width: '100%' // Take full width
                                                    }}
                                                >
                                                    {step.label}
                                                </Typography>
                                            </Zoom>

                                            <Fade in={activeStep === index} timeout={1000}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        mb: 4,
                                                        opacity: 0.9,
                                                        maxWidth: '400px',
                                                        overflow: 'hidden' // ✅ added
                                                    }}
                                                >
                                                    {step.description}
                                                </Typography>
                                            </Fade>

                                            <Grow in={activeStep === index} timeout={1200}>
                                                <Button
                                                    variant="outlined"
                                                    sx={{
                                                        color: 'white',
                                                        borderColor: 'rgba(255,255,255,0.7)',
                                                        borderRadius: '50px',
                                                        px: 4,
                                                        py: 1,
                                                        mb: 1,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                                            borderColor: 'white'
                                                        },
                                                        overflow: 'hidden' // ✅ added here
                                                    }}
                                                >
                                                    {step.buttonText}
                                                </Button>
                                            </Grow>
                                        </Box>
                                    ))}
                                </AutoPlaySwipeableViews>

                                <MobileStepper
                                    steps={carouselItems.length}
                                    position="static"
                                    activeStep={activeStep}
                                    sx={{
                                        background: 'transparent',
                                        justifyContent: 'center',
                                        mt: 1,
                                        overflow: 'hidden', // ✅ added here
                                        '& .MuiMobileStepper-dot': {
                                            width: 10,
                                            height: 10,
                                            backgroundColor: 'rgba(255,255,255,0.4)',
                                            transition: 'all 0.3s ease',
                                            mx: 1
                                        },
                                        '& .MuiMobileStepper-dotActive': {
                                            backgroundColor: 'white',
                                            width: 24,
                                            borderRadius: '4px'
                                        }
                                    }}
                                    nextButton={null}
                                    backButton={null}
                                />
                            </Grid>
                        )}

                    </Grid>
                </Paper>
            </Grow>
        </Box>
    );
};

export default LoginScreen;